'use client';

import HomePageTitle from '@/app/[device]/[botType]/(home-page)/components/HomePageTitle';
import MagicJump from '@/components/MagicJump';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import Image from '@/components/Image';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { CategoryNavBigProps, JumpTargetItem, TrackingParams } from './types';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useMergedRefs } from '@/hooks/useMergedRefs';

/**
 * 生成CategoryNavBig专用的alt属性 - 参考Vue版本的fourImageAlt
 */
const fourImageAlt = (index: number): string => {
  const altArr = ['Best Deals', 'All coupons', '30-Day', 'Google reviews'];
  return altArr[index] || `Navigation item ${index + 1}`;
};

/**
 * 埋点参数Hook
 */
const useTrackingParams = (
  item: JumpTargetItem,
  index: number,
  moduleIndex: number,
  componentId: string,
  moduleTitle: string,
  moduleId: string | number,
): TrackingParams => {
  return useMemo(
    () => ({
      module_index: moduleIndex,
      module_name: componentId,
      module_id: String(moduleId),
      mkt_index: index,
      mkt_index_sub: '',
      mkt_jump_type: `${item.jump_type}`,
      mkt_jump_content: `${item.Jump_target}`,
      module_tab_name: '',
      coupon_id_list: '',
      coupon_name_list: '',
      shop_sku: `${item.sku?.shop_sku}`,
      business_sku: `${item.sku?.business_sku}`,
      product_sku: `${item.sku?.sku}`,
      product_id: `${item.sku?.id}`,
      product_name: `${item.sku?.short_name}`,
      group_id: `${item.product_group?.id}`,
      group_name: `${item.product_group?.name}`,
      module_title: moduleTitle,
    }),
    [item, index, moduleIndex, componentId, moduleTitle, moduleId],
  );
};

/**
 * 埋点逻辑Hook
 */
const useTrackingHandlers = (
  trackingParams: TrackingParams,
  onTrack?: (params: TrackingParams) => void,
  onExpTrack?: (params: TrackingParams) => void,
  onValidExpTrack?: (params: TrackingParams) => void,
) => {
  // 曝光埋点
  const [expRef, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  // 处理曝光埋点
  useEffect(() => {
    if (inView && onExpTrack) {
      try {
        onExpTrack(trackingParams);
      } catch (error) {
        logger.error('Navigation exposure tracking error:', error);
      }
    }
  }, [inView, onExpTrack, trackingParams]);

  // 处理点击埋点
  const handleClick = useCallback(() => {
    if (onTrack) {
      try {
        onTrack(trackingParams);
      } catch (error) {
        logger.error('Navigation click tracking error:', error);
      }
    }
  }, [onTrack, trackingParams]);

  const { ref: validExposureRef } = useValidExposure({
    callback: () => onValidExpTrack?.(trackingParams),
  });

  return { expRef, handleClick, validExposureRef };
};

/**
 * 导航项组件 - 响应式统一实现
 */
const NavigationItem = ({
  item,
  index,
  onTrack,
  onExpTrack,
  onValidExpTrack,
  moduleIndex,
  componentId,
  moduleTitle,
  indexData,
}: {
  item: JumpTargetItem;
  index: number;
  onTrack?: (params: TrackingParams) => void;
  onExpTrack?: (params: TrackingParams) => void;
  onValidExpTrack?: (params: TrackingParams) => void;
  moduleIndex: number;
  componentId: string;
  moduleTitle: string;
  indexData: any;
}) => {
  const { deviceType } = useContext(ResponsiveContext);
  const isPC = deviceType === EPlatform.WEB;

  // 获取埋点参数
  const trackingParams = useTrackingParams(
    item,
    index,
    moduleIndex,
    componentId,
    moduleTitle,
    indexData?.blocks_advances_id,
  );

  // 获取埋点处理函数
  const { expRef, handleClick, validExposureRef } = useTrackingHandlers(
    trackingParams,
    onTrack,
    onExpTrack,
    onValidExpTrack,
  );

  // MagicJump props
  const jumpProps = useMemo(
    () => ({
      jump_type: Number(item.jump_type),
      Jump_target: item.Jump_target,
      ...(item.sku?.shop_sku && { sku: { shop_sku: item.sku.shop_sku } }),
      ...(item.product_group?.id && {
        product_group: { id: item.product_group.id, path: item.product_group.path },
      }),
      ...(item.crowd_recommend?.id && { crowd_recommend: { id: item.crowd_recommend.id } }),
    }),
    [item],
  );
  const navigationItemRef = useMergedRefs(expRef, validExposureRef);

  return (
    <div ref={navigationItemRef}>
      <MagicJump
        prefetch
        item={jumpProps}
        onBeforeJump={handleClick}
        aria-label={fourImageAlt(index)}
        className="cursor-pointer block"
      >
        {isPC ? (
          // PC端：只有图片，横向排列
          <div className="flex items-center justify-center">
            <Image
              src={item.jump_image}
              alt={fourImageAlt(index)}
              width={item.jump_img_width || 219}
              height={item.jump_img_height || 48}
              loading={moduleIndex < 3 ? 'eager' : 'lazy'}
              className="h-[30px] w-auto lg:h-[42px] 2xl:h-[48px] object-contain block"
              style={{
                aspectRatio: item.jump_img_ratio || 'auto',
              }}
            />
          </div>
        ) : (
          // 移动端：垂直布局 - 图片在上，文字在下
          <div className="flex flex-col">
            <Image
              src={item.jump_image}
              alt={fourImageAlt(index)}
              width={200}
              height={150}
              loading="eager"
              className="w-full h-auto object-cover block"
              style={{
                aspectRatio: item.jump_img_ratio || 'auto',
              }}
            />
            {/* {item.title && (
              <span
                className="mt-2 text-[12px] leading-[14px] text-center"
                style={{ color: item.text_color || '#282828' }}
                dangerouslySetInnerHTML={{ __html: item.title }}
              />
            )} */}
          </div>
        )}
      </MagicJump>
    </div>
  );
};

/**
 * 大图分类导航组件 - 基于原Vue组件的样式迁移
 */
export default function CategoryNavBig({
  indexData,
  moduleIndex,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: CategoryNavBigProps) {
  const { deviceType } = useContext(ResponsiveContext);
  const isPC = deviceType === EPlatform.WEB;

  // 容器背景色 - 只有PC端有背景色
  const containerStyle = useMemo(() => {
    return {
      backgroundColor: isPC ? indexData.bg_color || undefined : undefined,
    };
  }, [indexData.bg_color, isPC]);

  // 如果没有数据，不渲染
  if (!indexData?.jump_target?.length) {
    return null;
  }

  return (
    <div className={`w-full`} style={containerStyle}>
      <div
        className={`
          ${isPC ? '' : 'my-[40px]'}
        `}
      >
        {/* 标题部分 - 只有移动端显示标题 */}
        <HomePageTitle
          indexData={indexData}
          variant="default"
          displayCondition="mobile-only"
          titleTag="h1"
          subtitleTag="h5"
          hasButton={false}
        />

        {/* 导航项列表容器 */}
        <div
          className={`
            ${
              isPC
                ? 'flex flex-wrap items-center justify-center pt-[15px] pb-[15px] px-[20px] lg:pt-[21px] lg:pb-[21px] lg:px-[48px] 2xl:pt-[21px] 2xl:pb-[21px] 2xl:px-[80px] gap-y-[15px] gap-x-[20px] lg:gap-y-[21px] lg:gap-x-[60px] 2xl:gap-y-[21px] 2xl:gap-x-[80px]'
                : 'px-[14px] grid grid-cols-2 gap-[7px] mt-[20px]'
            }
          `}
        >
          {indexData.jump_target.map((item, index) => (
            <NavigationItem
              key={item.id || index}
              item={item}
              index={index}
              onTrack={onTrack}
              onExpTrack={onExpTrack}
              onValidExpTrack={onValidExpTrack}
              moduleIndex={moduleIndex}
              componentId={indexData.component_id}
              moduleTitle={indexData.layout_title}
              indexData={indexData}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
